/**
 * "Seed UILM Keys" button + handler. Pushes every missing key to UILM,
 * publishes the file, and back-fills the matching `uilm*Key` UDS fields.
 * Used after a legacy template gets a UILM module for the first time, or
 * when an admin adds entities while offline / a previous sync failed.
 */

import { useState } from 'react';
import { Loader2, Sprout } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { DropdownMenuItem } from '@/components/ui-kit/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { seedTemplateUilm } from '../../lib/sync-template-uilm';

interface TranslationTabSeedActionProps {
  templateId: string;
  /** Count of keys that will be pushed. Drives the disabled / hint state. */
  missingCount: number;
  /** Called after a successful seed so the parent can refetch the diff. */
  onSeeded: () => void;
  /** When true, renders as a DropdownMenuItem (used inside the mobile
      three-dot menu) instead of the default standalone Button. */
  asMenuItem?: boolean;
}

export function TranslationTabSeedAction({
  templateId,
  missingCount,
  onSeeded,
  asMenuItem = false,
}: TranslationTabSeedActionProps) {
  const { toast } = useToast();
  const [running, setRunning] = useState(false);

  const handleSeed = async () => {
    setRunning(true);
    try {
      const result = await seedTemplateUilm(templateId);
      toast({
        variant: 'success',
        title: 'Seed complete',
        description:
          result.pushedKeys > 0
            ? `Pushed ${result.pushedKeys} new key${result.pushedKeys !== 1 ? 's' : ''} to UILM (${result.alreadyOnUilm} already existed).`
            : 'Nothing to push — every local key was already on UILM.',
      });
      onSeeded();
    } catch (err) {
      console.error('Seed failed:', err);
      toast({
        variant: 'destructive',
        title: 'Seed failed',
        description: err instanceof Error ? err.message : 'Could not push keys to UILM.',
      });
    } finally {
      setRunning(false);
    }
  };

  const disabled = running || missingCount === 0;
  const icon = running ? (
    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
  ) : (
    <Sprout className="h-4 w-4 mr-2" />
  );
  const label = running
    ? 'Seeding…'
    : missingCount > 0
      ? `Seed ${missingCount} key${missingCount !== 1 ? 's' : ''}`
      : 'Nothing to seed';

  if (asMenuItem) {
    return (
      <DropdownMenuItem
        disabled={disabled}
        onSelect={(e) => {
          // Prevent the menu from closing while the seed is in flight —
          // toasts surface the outcome regardless.
          e.preventDefault();
          handleSeed();
        }}
      >
        {icon}
        {label}
      </DropdownMenuItem>
    );
  }

  return (
    <Button size="sm" onClick={handleSeed} disabled={disabled} variant="primary">
      {icon}
      {label}
    </Button>
  );
}
